/* eslint-disable react-hooks/exhaustive-deps */
import { omitRecursively, useTelemetryContext } from '@access-erp/ui-components';
import { useCallback } from 'react';

import { GetUserConfiguration, GetUserConfigurationVariables }
  from '@/graphql/contracts/_generated_/getUserConfiguration.query';
import GetUserConfigurationQuery from '@/graphql/contracts/getUserConfiguration.query.gql';
import { SaveUserConfiguration } from '@/graphql/contracts/_generated_/saveUserConfiguration.mutation';
import { DeleteUserConfiguration } from '@/graphql/contracts/_generated_/deleteUserConfiguration.mutation';
import { useSaveUserConfiguration } from './useSaveUserConfiguration';
import { useDeleteUserConfiguration } from './useDeleteUserConfiguration';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';

const UNEXPECTED_MUTATION_RESPONSE = 'Unexpected response';

type UseUserConfigurationDataReturn = {
  getConfiguration: (userName: string, formName: string) => Promise<ApiUserConfiguration>;
  saveConfiguration: (userName: string, newConfiguration: ApiUserConfiguration) => Promise<boolean>;
  deleteConfiguration: (userName: string, formName: string) => Promise<boolean>;
};

const mapQuery = (result: GetUserConfiguration | null | undefined) =>
  result?.config?.userConfiguration?.userConfigurationByRowKey;

const mapWrite = (result: SaveUserConfiguration | null | undefined) =>
  result?.config?.userConfiguration?.writeUserConfiguration;

const mapDelete = (result: DeleteUserConfiguration | null | undefined) =>
  result?.config?.userConfiguration?.deleteUserConfiguration;

export const useUserConfigurationData = (
  localConfiguration: Record<string, ApiUserConfiguration>,
): UseUserConfigurationDataReturn => {
  const { logEvent } = useTelemetryContext();
  const [saveUserConfiguration] = useSaveUserConfiguration();
  const [deleteUserConfiguration] = useDeleteUserConfiguration();

  const getConfiguration = useCallback(
    async (userName: string, formName: string) => {
      const result = await apolloClientContracts.query<GetUserConfiguration, GetUserConfigurationVariables>({
        query: GetUserConfigurationQuery,
        variables: { userName, formName },
        context: { important: true },
      });

      const data = mapQuery(result.data);

      return omitRecursively(data ?? {}, '__typename');
    },
    [localConfiguration],
  );

  const saveConfiguration = useCallback(
    async (userName: string, newConfiguration: ApiUserConfiguration) => {
      const parsedConfiguration = omitRecursively(
        { ...newConfiguration, username: userName },
        '__typename',
      );

      const response = await saveUserConfiguration({
        variables: { configuration: parsedConfiguration },
      });
      const data = mapWrite(response.data);

      switch (data?.__typename) {
        case 'Succeeded':
          return data.succeeded;
        case 'UnexpectedError':
          logEvent?.('Unexpected error while saving user configuration', {
            ...newConfiguration,
            errorMessage: data.errorMessage,
          });
          return false;
        default:
          logEvent?.('Unexpected error while saving user configuration', {
            ...newConfiguration,
            errorMessage: UNEXPECTED_MUTATION_RESPONSE,
          });
          return false;
      }
    },
    [],
  );

  const deleteConfiguration = useCallback(async (userName: string, formName: string) => {
    const response = await deleteUserConfiguration({
      variables: { userName, formName },
    });
    const data = mapDelete(response.data);

    switch (data?.__typename) {
      case 'Succeeded':
        return data.succeeded;
      case 'UnexpectedError':
        logEvent?.('Unexpected error while deleting user configuration', {
          userName,
          formName,
          errorMessage: data.errorMessage,
        });
        return false;
      default:
        logEvent?.('Unexpected error while deleting user configuration', {
          userName,
          formName,
          errorMessage: UNEXPECTED_MUTATION_RESPONSE,
        });
        return false;
    }
  }, []);

  return {
    getConfiguration,
    saveConfiguration,
    deleteConfiguration,
  };
};

export type ApiUserConfiguration = ReturnType<typeof mapQuery>;
